import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';

export interface Breadcrumb {
    label: string;
    url: string;
}

@Injectable({
    providedIn: 'root'
})
export class BreadcrumbService {
    private breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

    constructor(private router: Router) {
        // Initialize breadcrumbs on service construction (handles page refresh)
        const initialBreadcrumbs = this.createBreadcrumbs(this.router.routerState.snapshot.root);
        this.breadcrumbs$.next(initialBreadcrumbs);

        // Update breadcrumbs on navigation
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                const breadcrumbs = this.createBreadcrumbs(this.router.routerState.snapshot.root);
                this.breadcrumbs$.next(breadcrumbs);
            });
    }

    get breadcrumbs() {
        return this.breadcrumbs$.asObservable();
    }

    private createBreadcrumbs(
        route: ActivatedRouteSnapshot,
        url: string = '',
        breadcrumbs: Breadcrumb[] = []
    ): Breadcrumb[] {
        const children: ActivatedRouteSnapshot[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            const routeURL: string = child.url.map(segment => segment.path).join('/');
            if (routeURL !== '') {
                url += `/${routeURL}`;
            }

            // Get breadcrumb label from route data
            const label = child.data['breadcrumb'];
            if (label) {
                breadcrumbs.push({ label, url });
            }

            return this.createBreadcrumbs(child, url, breadcrumbs);
        }

        return breadcrumbs;
    }

    setBreadcrumbs(breadcrumbs: Breadcrumb[]) {
        this.breadcrumbs$.next(breadcrumbs);
    }
}

